import { useCallback } from 'react'
import {
  EntitledCatalogueEntityType,
  EntitledCatalogueType,
  EntitledCatalogueSelectionMode,
  DiscountFieldPaths,
  DiscountFormV2StateType,
  BundleEntitlement,
  BxgyEntitlement,
  AmountEntitlement,
  ExcludedCatalogueType
} from 'src/lib/types/discountsV2'
import { CatalogueDataType } from 'src/lib/types/common/catalogue'

type ProductDiscountEntitlement = BundleEntitlement | BxgyEntitlement | AmountEntitlement

interface UseEntitledProductsProps {
  entitlement: ProductDiscountEntitlement
  setFormState: (payload: Partial<DiscountFormV2StateType>, clearPaths?: string[]) => void
  catalogueType: 'COLLECTION' | 'PRODUCT_AND_VARIANT'
}

export const useEntitledProducts = ({ entitlement, setFormState, catalogueType }: UseEntitledProductsProps) => {
  const { entitledCatalogue, metaData, excludedCatalogue } = entitlement
  const items = metaData?.entitledCatalogue || []

  const updateAndClearErrors = useCallback(
    (payload: Partial<DiscountFormV2StateType>, groupIndex: number, newCount: number, oldCount: number) => {
      const maxEntityCount = Math.max(newCount, oldCount)
      const entityPaths = Array.from(
        { length: maxEntityCount },
        (_, i) => `${DiscountFieldPaths.ENTITLED_CATALOGUE}.${groupIndex}.entities.${i}`
      )
      setFormState(payload, [
        DiscountFieldPaths.ENTITLED_CATALOGUE,
        `${DiscountFieldPaths.ENTITLED_CATALOGUE}.${groupIndex}.entities`,
        ...entityPaths
      ])
    },
    [setFormState]
  )

  const handleItemsSelected = useCallback(
    (products: CatalogueDataType[], excludedProducts: CatalogueDataType[] = [], groupIndex: number) => {
      const mapToEntity = (product: CatalogueDataType) => {
        const isAllSelector = product.id === 'shopflo-all-products' || product.id === 'shopflo-all-collections'

        if (catalogueType === 'COLLECTION') {
          return {
            type: EntitledCatalogueEntityType.COLLECTION,
            entityId: isAllSelector ? [] : [product.id || '']
          }
        }

        const selectedVariants = product.variants?.filter(Boolean) || []
        const totalVariants = product.totalVariantCount || 0
        const isPartial = selectedVariants.length > 0 && selectedVariants.length < totalVariants

        if (isPartial) {
          return {
            type: EntitledCatalogueEntityType.VARIANT,
            entityId: selectedVariants.map(variant => variant.id || '')
          }
        }

        return {
          type: EntitledCatalogueEntityType.PRODUCT,
          entityId: isAllSelector ? [] : [product.id || '']
        }
      }

      const nextIncludedEntities = products.map(mapToEntity)
      const nextExcludedEntities = excludedProducts.map(mapToEntity)

      const nextEntitledCatalogue = [...entitledCatalogue]
      const nextExcludedCatalogue = [...(excludedCatalogue || [])]
      const nextEntitledMetaData = [...(items || [])]
      const nextExcludedMetaData = [...(metaData?.excludedCatalogue || [])]

      const existingGroup = nextEntitledCatalogue[groupIndex]

      // Update Included Group
      nextEntitledCatalogue[groupIndex] = existingGroup
        ? { ...existingGroup, entities: nextIncludedEntities }
        : {
            quantity: 1,
            selectionMode: EntitledCatalogueSelectionMode.ANY,
            entities: nextIncludedEntities
          }
      nextEntitledMetaData[groupIndex] = products

      // Update Excluded Group
      nextExcludedCatalogue[groupIndex] = { entities: nextExcludedEntities }
      nextExcludedMetaData[groupIndex] = excludedProducts

      const totalCount = nextEntitledCatalogue.reduce(
        (sum: number, group: EntitledCatalogueType) => sum + (group.quantity || 0),
        0
      )

      updateAndClearErrors(
        {
          entitlement: {
            ...entitlement,
            entitledCatalogue: nextEntitledCatalogue,
            excludedCatalogue: nextExcludedCatalogue,
            entitledProductCount: totalCount,
            metaData: {
              ...entitlement.metaData,
              entitledCatalogue: nextEntitledMetaData,
              excludedCatalogue: nextExcludedMetaData
            }
          }
        },
        groupIndex,
        nextIncludedEntities.length,
        existingGroup?.entities?.length || 0
      )
    },
    [
      entitledCatalogue,
      items,
      excludedCatalogue,
      metaData,
      catalogueType,
      setFormState,
      entitlement,
      updateAndClearErrors
    ]
  )

  const handleRemoveGroup = useCallback(
    (index: number) => {
      const newCatalogue = [...entitledCatalogue]
      newCatalogue.splice(index, 1)

      const newMetaDataCatalogue = [...items]
      newMetaDataCatalogue.splice(index, 1)

      const newExcludedCatalogue = [...(excludedCatalogue || [])]
      // Try to remove excluded group if it matches index (usually safe assumption for parallel arrays)
      if (index < newExcludedCatalogue.length) {
        newExcludedCatalogue.splice(index, 1)
      }

      const newExcludedMetaDataCatalogue = [...(metaData?.excludedCatalogue || [])]
      if (index < newExcludedMetaDataCatalogue.length) {
        newExcludedMetaDataCatalogue.splice(index, 1)
      }

      if (newCatalogue.length === 0) {
        newCatalogue.push({
          quantity: 1,
          entities: [],
          selectionMode: EntitledCatalogueSelectionMode.ANY
        })
        newMetaDataCatalogue.push([])
      }

      setFormState(
        {
          entitlement: {
            ...entitlement,
            entitledCatalogue: newCatalogue,
            excludedCatalogue: newExcludedCatalogue,
            metaData: {
              ...entitlement.metaData,
              entitledCatalogue: newMetaDataCatalogue,
              excludedCatalogue: newExcludedMetaDataCatalogue
            }
          }
        },
        [DiscountFieldPaths.ENTITLED_CATALOGUE]
      )
    },
    [entitledCatalogue, items, excludedCatalogue, metaData, setFormState, entitlement]
  )

  const handleRemoveIncludedItem = useCallback(
    (groupIndex: number, itemIndex: number) => {
      const nextEntitledCatalogue = [...entitledCatalogue]
      const nextEntitledMetaData = [...(metaData?.entitledCatalogue || [])]

      const targetGroup = { ...nextEntitledCatalogue[groupIndex] }
      targetGroup.entities = [...targetGroup.entities]
      targetGroup.entities.splice(itemIndex, 1)
      nextEntitledCatalogue[groupIndex] = targetGroup

      const targetMetaGroup = [...(nextEntitledMetaData[groupIndex] || [])]
      targetMetaGroup.splice(itemIndex, 1)
      nextEntitledMetaData[groupIndex] = targetMetaGroup

      const totalCount = nextEntitledCatalogue.reduce(
        (sum: number, group: EntitledCatalogueType) => sum + (group.quantity || 0),
        0
      )

      const nextExcludedCatalogue = [...(excludedCatalogue || [])]
      const nextExcludedMetaData = [...(metaData?.excludedCatalogue || [])]

      if (targetGroup.entities.length === 0) {
        // If included group is empty, clear the excluded group for this index
        if (nextExcludedCatalogue[groupIndex]) {
          nextExcludedCatalogue[groupIndex] = {
            entities: []
          }
        }
        if (nextExcludedMetaData[groupIndex]) {
          nextExcludedMetaData[groupIndex] = []
        }
      }

      updateAndClearErrors(
        {
          entitlement: {
            ...entitlement,
            entitledCatalogue: nextEntitledCatalogue,
            entitledProductCount: totalCount,
            excludedCatalogue: nextExcludedCatalogue,
            metaData: {
              ...entitlement.metaData,
              entitledCatalogue: nextEntitledMetaData,
              excludedCatalogue: nextExcludedMetaData
            }
          }
        },
        groupIndex,
        targetGroup.entities.length,
        entitledCatalogue[groupIndex]?.entities.length || 0
      )
    },
    [entitledCatalogue, metaData, entitlement, excludedCatalogue, updateAndClearErrors]
  )

  const handleRemoveExcludedItem = useCallback(
    (groupIndex: number, itemIndex: number) => {
      const includedLength = entitledCatalogue[groupIndex]?.entities?.length ?? 0
      const excludedIndex = itemIndex - includedLength
      if (excludedIndex < 0) return

      const nextExcludedCatalogue = [...(excludedCatalogue || [])]
      const nextExcludedMetaData = [...(metaData?.excludedCatalogue || [])]

      const targetGroup = nextExcludedCatalogue[groupIndex]

      if (targetGroup && !Array.isArray(targetGroup)) {
        const updatedGroup = { ...targetGroup }
        updatedGroup.entities = [...updatedGroup.entities]
        updatedGroup.entities.splice(excludedIndex, 1)
        nextExcludedCatalogue[groupIndex] = updatedGroup
      }

      if (nextExcludedMetaData[groupIndex]) {
        const targetMetaGroup = [...(nextExcludedMetaData[groupIndex] || [])]
        targetMetaGroup.splice(excludedIndex, 1)
        nextExcludedMetaData[groupIndex] = targetMetaGroup
      }

      setFormState({
        entitlement: {
          ...entitlement,
          excludedCatalogue: nextExcludedCatalogue,
          metaData: {
            ...entitlement.metaData,
            excludedCatalogue: nextExcludedMetaData
          }
        }
      })
    },
    [entitledCatalogue, excludedCatalogue, metaData, entitlement, setFormState]
  )

  const handleQuantityChange = useCallback(
    (index: number, value?: string, mode?: EntitledCatalogueSelectionMode) => {
      const newEntitlements = [...entitledCatalogue]

      if (newEntitlements[index]) {
        const updatedItem = { ...newEntitlements[index] }

        if (value === undefined || mode === EntitledCatalogueSelectionMode.ALL) {
          updatedItem.quantity = null
        } else {
          const parsedValue = parseInt(value)
          updatedItem.quantity = isNaN(parsedValue) ? 0 : Math.min(parsedValue, 999999)
        }

        if (mode) {
          updatedItem.selectionMode = mode
        }

        newEntitlements[index] = updatedItem

        let totalCount = newEntitlements.reduce(
          (sum: number, group: EntitledCatalogueType) => sum + (group.quantity || 0),
          0
        )

        if (updatedItem.selectionMode === EntitledCatalogueSelectionMode.ALL) {
          totalCount = -1
        }

        setFormState(
          {
            entitlement: {
              ...entitlement,
              entitledCatalogue: newEntitlements,
              entitledProductCount: totalCount
            }
          },
          [`${DiscountFieldPaths.ENTITLED_CATALOGUE}.${index}.quantity`]
        )
      }
    },
    [entitledCatalogue, setFormState, entitlement]
  )

  const handleAddEmptyGroup = useCallback(() => {
    const newEntitlementEntity: EntitledCatalogueType = {
      quantity: 1,
      entities: [],
      selectionMode: EntitledCatalogueSelectionMode.ANY
    }
    const newMetaItems: CatalogueDataType[] = []

    const newCatalogue = [...entitledCatalogue, newEntitlementEntity]
    const totalCount = newCatalogue.reduce(
      (sum: number, group: EntitledCatalogueType) => sum + (group.quantity || 0),
      0
    )

    setFormState(
      {
        entitlement: {
          ...entitlement,
          entitledCatalogue: newCatalogue,
          entitledProductCount: totalCount,
          metaData: {
            ...entitlement.metaData,
            entitledCatalogue: [...items, newMetaItems]
          }
        }
      },
      [DiscountFieldPaths.ENTITLED_CATALOGUE]
    )
  }, [entitledCatalogue, items, setFormState, entitlement])

  const handleVariantUpdate = useCallback(
    (updatedProduct: CatalogueDataType, groupIndex: number, itemIndex: number) => {
      const includedLength = entitledCatalogue[groupIndex]?.entities?.length || 0

      if (itemIndex < includedLength) {
        const newCatalogue = [...entitledCatalogue]
        const group = { ...newCatalogue[groupIndex] }
        group.entities = [...group.entities]

        const selectedVariantIds = updatedProduct.variants?.map(variant => variant.id || '').filter(Boolean) || []
        const totalVariants = updatedProduct.totalVariantCount || 0
        const isPartial = selectedVariantIds.length > 0 && selectedVariantIds.length < totalVariants

        const newType = isPartial ? EntitledCatalogueEntityType.VARIANT : EntitledCatalogueEntityType.PRODUCT
        const newEntityId = isPartial ? selectedVariantIds : [updatedProduct.id || '']

        group.entities[itemIndex] = {
          ...group.entities[itemIndex],
          type: newType,
          entityId: newEntityId
        }
        newCatalogue[groupIndex] = group

        const newMetaDataCatalogue = [...items]
        const metaGroup = [...newMetaDataCatalogue[groupIndex]]

        metaGroup[itemIndex] = {
          ...metaGroup[itemIndex],
          variants: updatedProduct.variants?.map(variant => ({
            id: variant.id || '',
            price: variant.price || 0,
            inventory: variant.inventory || 0,
            isVariant: true,
            description: variant.description || '',
            imageUrl: variant.imageUrl,
            parentDetails: {
              id: updatedProduct.id || '',
              name: updatedProduct.description || ''
            }
          })),
          id: updatedProduct.id || '',
          description: updatedProduct.description || '',
          isVariant: false,
          imageUrl: updatedProduct.imageUrl,
          totalVariantCount: updatedProduct.totalVariantCount
        }
        newMetaDataCatalogue[groupIndex] = metaGroup

        updateAndClearErrors(
          {
            entitlement: {
              ...entitlement,
              entitledCatalogue: newCatalogue,
              metaData: {
                ...entitlement.metaData,
                entitledCatalogue: newMetaDataCatalogue
              }
            }
          },
          groupIndex,
          group.entities.length,
          includedLength
        )
      } else {
        const adjustedIndex = itemIndex - includedLength
        const newExcludedCatalogue = [...(excludedCatalogue || [])]
        const excludedGroup = newExcludedCatalogue[groupIndex]

        if (excludedGroup && !Array.isArray(excludedGroup)) {
          const group = { ...excludedGroup }
          group.entities = [...group.entities]

          const selectedVariantIds = updatedProduct.variants?.map(v => v.id || '').filter(Boolean) || []
          const totalVariants = updatedProduct.totalVariantCount || 0
          const isPartial = selectedVariantIds.length > 0 && selectedVariantIds.length < totalVariants

          const newType = isPartial ? EntitledCatalogueEntityType.VARIANT : EntitledCatalogueEntityType.PRODUCT
          const newEntityId = isPartial ? selectedVariantIds : [updatedProduct.id || '']

          group.entities[adjustedIndex] = {
            ...group.entities[adjustedIndex],
            type: newType,
            entityId: newEntityId
          }
          newExcludedCatalogue[groupIndex] = group
        }

        const newMetaDataCatalogue = [...(metaData?.excludedCatalogue || [])]
        const metaGroup = [...(newMetaDataCatalogue[groupIndex] || [])]

        metaGroup[adjustedIndex] = {
          ...metaGroup[adjustedIndex],
          variants: updatedProduct.variants?.map(v => ({
            id: v.id || '',
            price: v.price || 0,
            inventory: v.inventory || 0,
            isVariant: true,
            description: v.description || '',
            imageUrl: v.imageUrl,
            parentDetails: {
              id: updatedProduct.id || '',
              name: updatedProduct.description || ''
            }
          })),
          id: updatedProduct.id || '',
          description: updatedProduct.description || '',
          imageUrl: updatedProduct.imageUrl,
          totalVariantCount: updatedProduct.totalVariantCount
        }
        newMetaDataCatalogue[groupIndex] = metaGroup

        setFormState({
          entitlement: {
            ...entitlement,
            excludedCatalogue: newExcludedCatalogue,
            metaData: {
              ...entitlement.metaData,
              excludedCatalogue: newMetaDataCatalogue
            }
          }
        })
      }
    },
    [entitledCatalogue, excludedCatalogue, items, metaData, setFormState, entitlement, updateAndClearErrors]
  )

  return {
    handleItemsSelected,
    handleRemoveGroup,
    handleRemoveIncludedItem,
    handleRemoveExcludedItem,
    handleQuantityChange,
    handleAddEmptyGroup,
    handleVariantUpdate
  }
}
